import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Sun, Moon, LogIn, UserPlus, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { afterSignInPath } from '../lib/authApi';
import { rememberReferralCode, lookupReferral, cleanReferralCode, pendingReferralCode } from '../lib/billingApi';
import { WannaPublishLockup, WannaPublishMark } from '../components/editor/WannaPublishLogo';
import { SiteFooter } from '../components/legal/SiteFooter';
import { Button } from '../components/ui/button';
import { DemoHintProvider } from '../components/landing/DemoHint';
import { DemoWorkspace } from '../components/landing/DemoWorkspace';
import { DemoPage } from '../components/landing/DemoPage';
import { DEFAULT_TEMPLATE_ID, templateById, initialObject, FONT_SCHEMES, COLOUR_SCHEMES, clampZoom } from '../components/landing/demoTemplates';

const THEME_KEY = 'wp.theme'; // 'dark' | 'light'; absent = follow the system. The editor reads the same key.
const COMPACT_QUERY = '(max-width: 899px)';

const readTheme = () => {
  try { const v = localStorage.getItem(THEME_KEY); if (v === 'dark' || v === 'light') return v; } catch { /* private mode */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
};

/**
 * Landing page: a short header (wordmark, tagline, theme, sign-in switch) above an app window that contains the
 * editor's workspace — ribbon, rulers, a real-size page, the template sorter and status bar. On the page the pitch is
 * typeset as a small publication and the sign-in card sits as a selected text box that visitors can resize and rotate.
 * Every other command is inert but explains itself on hover; a click nudges towards signing in. Nobody reaches the
 * editor without an account (no guest mode), so this is also the sign-in page.
 */
export default function Landing() {
  const { user, loading, expiredNotice } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(location.state?.forgot ? 'forgot' : 'signin'); // 'signin' | 'signup' | 'forgot'
  const [theme, setTheme] = useState(readTheme);
  const [compact, setCompact] = useState(() => !!window.matchMedia?.(COMPACT_QUERY)?.matches);
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE_ID);
  const template = templateById(templateId);
  const [obj, setObj] = useState(() => initialObject(templateById(DEFAULT_TEMPLATE_ID)));
  const [objH, setObjH] = useState(0);
  const [zoom, setZoom] = useState(1); // 100 % on load (user's choice); slider / −+ / fit / Ctrl+wheel change it
  const [preview, setPreview] = useState(null); // { kind: 'fonts' | 'colours', idx } while a Schemes command is hovered
  const initialError = location.state?.authError || '';
  const linkParams = new URLSearchParams(location.search);
  const sharedProject = linkParams.get('share') || linkParams.get('project');
  const refCode = cleanReferralCode(linkParams.get('ref'));
  const [invite, setInvite] = useState(null); // { valid, name } once a ?ref= code (or one remembered earlier) has been looked up

  // A friend's invite link: remember the code until the account exists (register sends it; Google sign-up applies it
  // afterwards), find out whose it is for the note, and open the Create-account tab.
  useEffect(() => {
    const code = refCode || pendingReferralCode();
    if (!code) return undefined;
    if (refCode) rememberReferralCode(refCode);
    let cancelled = false;
    lookupReferral(code)
      .then((res) => { if (!cancelled) { setInvite({ valid: !!res.valid, name: res.referrer_first_name || '' }); if (res.valid && refCode) setMode('signup'); } })
      .catch(() => { if (!cancelled) setInvite(null); });
    return () => { cancelled = true; };
  }, [refCode]);

  // Theme: the visitor's choice wins; otherwise follow the system and keep following it while it changes.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = () => { try { if (localStorage.getItem(THEME_KEY)) return; } catch { /* ignore */ } setTheme(mq.matches ? 'dark' : 'light'); };
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode */ }
    setTheme(next);
  };

  useEffect(() => {
    const mq = window.matchMedia?.(COMPACT_QUERY);
    const onChange = () => setCompact(!!mq.matches);
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (location.state?.authError || location.state?.forgot) navigate(location.pathname + location.search, { replace: true, state: null });
    // once on arrival: the one-shot state (auth error / forgot-password notice) is consumed, then cleared from the history
    // entry; depending on location would re-run on the very navigation this issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTemplate = (id) => {
    if (id === templateId) return;
    const next = templateById(id);
    setTemplateId(next.id);
    setObj(initialObject(next));
    toast(`${next.name} template`, { id: 'demo-template', description: next.toast, duration: 4500 });
  };
  const resetObject = () => setObj(initialObject(template));
  const onMeasureObj = useCallback((h) => setObjH(h), []);
  const onZoomChange = useCallback((z) => setZoom(clampZoom(z)), []);

  // Scheme previews: hovering Page Design ▸ Fonts / Colour Schemes cycles the page through the schemes every second
  // and puts everything back on mouse-out.
  const onPreview = useCallback((kind, on) => setPreview(on ? { kind, idx: 0 } : null), []);
  const previewKind = preview?.kind || null;
  useEffect(() => {
    if (!previewKind) return undefined;
    const id = setInterval(() => setPreview((p) => (p ? { ...p, idx: p.idx + 1 } : p)), 1000);
    return () => clearInterval(id);
  }, [previewKind]);
  const fontScheme = previewKind === 'fonts' ? FONT_SCHEMES[preview.idx % FONT_SCHEMES.length] : null;
  const colourScheme = previewKind === 'colours' ? COLOUR_SCHEMES[preview.idx % COLOUR_SCHEMES.length] : null;
  const previewLabel = fontScheme
    ? { kind: 'fonts', text: `Font scheme · ${fontScheme.name} — ${fontScheme.fonts.set}` }
    : colourScheme
      ? { kind: 'colours', text: `Colour scheme · ${colourScheme.name}` }
      : null;

  if (!loading && user) return <Navigate to={afterSignInPath(location.search)} replace />;

  const notice = sharedProject
    ? { kind: 'shared', text: 'Someone shared a publication with you. Sign in (or create an account) and your copy opens straight away.' }
    : invite?.valid
      ? { kind: 'invite', text: `${invite.name || 'A friend'} invited you to WannaPublish — create your account and your first month is free (or $5 off the annual plan).` }
      : invite && !invite.valid && refCode
        ? { kind: 'invite-invalid', text: 'That invitation link is not valid any more — you can still create an account, and add a friend\u2019s code later when you pick a plan.' }
        : expiredNotice === 'deleted'
          ? { kind: 'deleted', text: 'Your account and everything in it were deleted. Thanks for using WannaPublish — you are welcome back any time.' }
          : expiredNotice
            ? { kind: 'expired', text: 'Your session ended — sign in again to continue where you left off.' }
            : null;

  const switchLabel = mode === 'signup' ? 'Sign in' : 'Create account';

  return (
    <main className="wp-landing h-[100dvh] min-h-[600px] flex flex-col bg-background text-foreground" data-testid="landing-page" data-theme={theme}>
      <header className="shrink-0 w-full max-w-[1480px] mx-auto px-3 sm:px-6 h-[72px] sm:h-[88px] xl:h-[96px] flex items-center justify-between gap-3" data-testid="landing-header">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0" data-testid="landing-brand">
          {compact ? <WannaPublishLockup size={52} className="shrink-0" /> : (
            <>
              <WannaPublishLockup size={64} className="shrink-0 xl:hidden" />
              <WannaPublishLockup size={72} className="shrink-0 hidden xl:block" />
            </>
          )}
          <div className="hidden md:flex flex-col justify-center min-w-0 border-l border-border pl-4 py-0.5" data-testid="landing-tagline">
            <span className="text-[15px] lg:text-[17px] font-medium text-foreground truncate" style={{ fontFamily: 'Outfit, Inter, sans-serif' }} data-testid="landing-tagline-main">Desktop publishing in your browser</span>
            <span className="text-[13px] lg:text-[15px] text-muted-foreground truncate" data-testid="landing-tagline-sub">Open and edit your classic Publisher files</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button asChild variant="ghost" className="hidden sm:inline-flex h-10 px-3 text-sm" data-testid="landing-about-link">
            <Link to="/about" title="About WannaPublish"><Info className="h-4 w-4 mr-2" aria-hidden="true" />About</Link>
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-10 w-10" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} data-testid="landing-theme-toggle">{theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}</Button>
          <Button type="button" variant="outline" className="h-10 px-4 text-sm" onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')} data-testid="landing-header-switch">
            {mode === 'signup' ? <LogIn className="h-4 w-4 mr-2" aria-hidden="true" /> : <UserPlus className="h-4 w-4 mr-2" aria-hidden="true" />}{switchLabel}
          </Button>
        </div>
      </header>

      <section className="flex-1 min-h-0 w-full max-w-[1480px] mx-auto px-3 sm:px-6 pb-1 flex flex-col" aria-label="Workspace preview">
        <div className="wp-demo-window flex-1 min-h-0 flex flex-col rounded-xl border border-border bg-[hsl(var(--wp-workspace))] overflow-hidden" data-testid="demo-window">
          <div className="h-9 shrink-0 flex items-center gap-2 px-3 border-b border-[hsl(var(--wp-ribbon-border))] bg-[hsl(var(--wp-ribbon-2))]" data-testid="demo-window-titlebar">
            <span className="flex items-center gap-1.5" aria-hidden="true">
              <i className="block h-3 w-3 rounded-full" style={{ background: 'hsl(var(--destructive))' }} />
              <i className="block h-3 w-3 rounded-full" style={{ background: 'hsl(var(--wp-context-amber))' }} />
              <i className="block h-3 w-3 rounded-full" style={{ background: 'hsl(var(--wp-context-green))' }} />
            </span>
            <span className="flex-1 min-w-0 flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground" data-testid="demo-window-title"><WannaPublishMark size={14} glow={false} className="shrink-0" title="" /><span className="truncate">Welcome.pub — WannaPublish</span></span>
            <span className="w-[54px] shrink-0" aria-hidden="true" />
          </div>
          <DemoHintProvider>
            <DemoWorkspace compact={compact} template={template} onSelectTemplate={selectTemplate} selection={{ w: obj.w, h: objH, rot: obj.rot }} zoom={zoom} onZoomChange={onZoomChange} previewLabel={previewLabel} onPreview={onPreview}>
              <DemoPage template={template} obj={obj} onObjChange={setObj} objH={objH} onMeasureObj={onMeasureObj} onResetObj={resetObject} mode={mode} onModeChange={setMode} initialError={initialError} notice={notice} compact={compact} zoom={zoom} fontScheme={fontScheme} colourScheme={colourScheme} />
            </DemoWorkspace>
          </DemoHintProvider>
        </div>
      </section>
      <SiteFooter compact testId="landing-footer" />
    </main>
  );
}
